import { useCallback, useRef } from 'react';

interface UseLongPressOptions {
  /** Hold duration before the press fires. */
  delayMs?: number;
  /** Movement (px) that cancels the press as a scroll/drag. */
  moveTolerance?: number;
  /**
   * Fired instead of `onLongPress` when the hold belongs to the SECOND tap of a
   * double-tap. Optional — omit it and the hook behaves as a plain long-press.
   */
  onDoubleTapHold?: (x: number, y: number) => void;
  /** Max gap between the first tap's release and the second tap's start. */
  doubleTapWindowMs?: number;
}

interface LongPressHandlers {
  onTouchStart: (e: React.TouchEvent) => void;
  onTouchMove: (e: React.TouchEvent) => void;
  onTouchEnd: (e: React.TouchEvent) => void;
  onTouchCancel: (e: React.TouchEvent) => void;
}

/**
 * Fires `onLongPress(x, y)` after the user holds a single-finger touch for
 * `delayMs` without moving more than `moveTolerance` px. Touch devices have no
 * right-click, so this is how the message context menu is surfaced on mobile.
 *
 * With `onDoubleTapHold` supplied, the same hold is routed to that callback
 * instead whenever it belongs to the second tap of a double-tap — i.e.
 * tap, then tap-and-hold. The two gestures share one timer deliberately: run as
 * separate hooks they would both fire on the second tap, and the context menu
 * would stomp whatever the double-tap was for.
 *
 * A double-tap is recognised when the second touch starts within
 * `doubleTapWindowMs` of the previous touch ending, near the same point. That
 * makes the first tap of the pair a plain tap — it must stay harmless, which it
 * is: a tap shorter than `delayMs` fires nothing.
 *
 * The press is cancelled by movement (a scroll/drag), an early release, a
 * second finger, or a cancelled touch. When it fires we `preventDefault()` on
 * the following `touchend` to suppress the browser's synthetic mouse events
 * (mousedown/click) — otherwise the phantom tap would immediately dismiss the
 * just-opened menu, land on a menu item, or collapse a fresh text selection.
 */
export function useLongPress(
  onLongPress: (x: number, y: number) => void,
  {
    delayMs = 450,
    moveTolerance = 10,
    onDoubleTapHold,
    doubleTapWindowMs = 320,
  }: UseLongPressOptions = {},
): LongPressHandlers {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startRef = useRef<{ x: number; y: number } | null>(null);
  const firedRef = useRef(false);
  /** End time + position of the previous touch, for double-tap detection. */
  const lastTapRef = useRef<{ t: number; x: number; y: number } | null>(null);

  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    startRef.current = null;
  }, []);

  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length !== 1) {
        cancel();
        return;
      }
      const t = e.touches[0];
      startRef.current = { x: t.clientX, y: t.clientY };
      firedRef.current = false;

      const prev = lastTapRef.current;
      const isSecondTap =
        !!onDoubleTapHold &&
        !!prev &&
        Date.now() - prev.t <= doubleTapWindowMs &&
        Math.abs(t.clientX - prev.x) <= moveTolerance * 2 &&
        Math.abs(t.clientY - prev.y) <= moveTolerance * 2;

      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        const s = startRef.current;
        if (!s) return;
        firedRef.current = true;
        // Consume the pair: a third tap starts a fresh sequence rather than
        // chaining another double-tap off the same first tap.
        lastTapRef.current = null;
        if (isSecondTap) {
          onDoubleTapHold!(s.x, s.y);
        } else {
          onLongPress(s.x, s.y);
        }
      }, delayMs);
    },
    [cancel, delayMs, moveTolerance, onLongPress, onDoubleTapHold, doubleTapWindowMs],
  );

  const onTouchMove = useCallback(
    (e: React.TouchEvent) => {
      const s = startRef.current;
      if (!s) return;
      const t = e.touches[0];
      if (Math.abs(t.clientX - s.x) > moveTolerance || Math.abs(t.clientY - s.y) > moveTolerance) {
        cancel();
        // A drag is not a tap — it must not seed the double-tap window.
        lastTapRef.current = null;
      }
    },
    [cancel, moveTolerance],
  );

  const onTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      const s = startRef.current;
      // Record the release only for a touch that ended WITHOUT firing — that's
      // a tap, and only a tap can be the first half of a double-tap.
      if (s && !firedRef.current) {
        lastTapRef.current = { t: Date.now(), x: s.x, y: s.y };
      }
      cancel();
      if (firedRef.current) {
        firedRef.current = false;
        e.preventDefault();
      }
    },
    [cancel],
  );

  return {
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onTouchCancel: onTouchEnd,
  };
}
